import "../chart.scss";

import type { EChartsOption } from "echarts";
import * as echarts from "echarts/core";
import ReactEcharts from "echarts-for-react";
import type { FC } from "react";
import { memo, useCallback, useEffect, useRef } from "react";

import { NoDataView } from "../../NoDataView/NoDataView";
import ChartLabel from "../ChartLabel";
import { commonProperties, labelOption } from "../constants";
import dark from "../themes/dark";
import light from "../themes/light";
import type { IChartProps } from "../types";

const initialOptions: EChartsOption = {
  ...commonProperties,
  shape: "arrow",
  xAxis: {
    type: "category",
    boundaryGap: false,
  },
  yAxis: {
    axisLabel: {
      showMaxLabel: false,
      showMinLabel: false,
    },
    boundaryGap: [0, "20%"],
    splitNumber: 3,
  },
  series: [],
};

const LineChart: FC<IChartProps> = memo(
  ({ themeMode, height, chartData, isLoading, toggleData }) => {
    const eChartsRef = useRef(null as any);
    echarts.registerTheme("dark", dark);
    echarts.registerTheme("light", light);

    useEffect(() => {
      if (chartData.series.length === 0) {
        return;
      }

      if (eChartsRef.current?.getEchartsInstance().getId()) {
        const chartId = eChartsRef.current?.getEchartsInstance().getId();
        document
          .querySelector(`[_echarts_instance_=${chartId}]`)
          ?.setAttribute("tabindex", "0");
      }
      // need to wait for the chart to be rendered before setting chart data
      setTimeout(() => {
        eChartsRef.current?.getEchartsInstance().setOption({
          tooltip: {
            trigger: "axis",
            axisPointer: {
              axis: "x",
              type: "line",
              lineStyle: {
                width: 2,
                type: "solid",
              },
              crossStyle: {
                color: "#000",
                cap: "square",
              },
            },
            className: "balance-tooltip",
            formatter: (params: any) => {
              const { yLabelFormatter, tooltipTitles, valueUnit } = chartData;
              return `<div>
                <div class="balance-tooltip__title">${
                  tooltipTitles[params[0].dataIndex] || params[0].name
                }</div>
                <div class="balance-tooltip__content">
                  <div class="balance-tooltip__item">
                    <div class="balance-tooltip__item__label">${
                      params[0].seriesName
                    }</div>
                    <div class="balance-tooltip__item__value"><b>${yLabelFormatter(
                      params[0].value,
                    )}${valueUnit}</b></div>
                  </div>
              </div>`;
            },
          },
          aria: {
            label: {
              general: {
                withoutTitle: chartData.label.label + " chart",
              },
            },
          },
          yAxis: {
            axisLabel: {
              formatter: chartData.yLabelFormatter,
            },
          },
          xAxis: {
            data: chartData.xAxisData,
            boundaryGap: true,
          },
          series: [
            {
              data: chartData.series[0].data,
              name: chartData.series[0].name,
              symbol: "none",
              type: "line",
              label: labelOption,
              lineStyle: {
                color: "#A347F8",
                width: 2,
              },
              triggerLineEvent: false,

              smooth: 0.5,
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: "#3C19CD",
                  },
                  {
                    offset: 1,
                    color: "rgba(60, 25, 205, 0.00)",
                  },
                ]),
              },
            },
          ],
        });
      }, 150);
    }, [themeMode, chartData]);

    return (
      <div className="chart chart__wrapper">
        <ChartLabel
          title={chartData.label.label}
          data={isLoading ? [] : chartData.label.values}
          dataToggler={toggleData}
        />
        {chartData.series.length === 0 || isLoading ? (
          <NoDataView isLoading={isLoading} />
        ) : (
          <ReactEcharts
            style={{ height }}
            echarts={echarts}
            option={initialOptions}
            opts={{ height: "auto" }}
            ref={eChartsRef}
            theme={themeMode}
            notMerge={true}
            lazyUpdate={true}
          />
        )}
      </div>
    );
  },
);

export { LineChart };
